import React, { forwardRef, Ref, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';

import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import EmbedModalWithGreenHeader from 'src/presentation/components/pages/shared/EmbedModalWithGreenHeader';
import StyledFooter from 'src/presentation/components/forms/Footer';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { ActionAdapter } from 'src/presentation/components/pages/glados/actionAdapters/ActionAdapters';

type ShowModalProps<T> = {
  params?: T;
  subscribe: (params: T) => void;
};

export type DuplicateModalWrapperRef = {
  show: ({
    params,
    subscribe,
  }: ShowModalProps<{ actionAdapter: Partial<ActionAdapter> & Pick<ActionAdapter, 'name'> }>) => void;
  close: () => void;
};

const DuplicateConfirmationModal = forwardRef((_, ref: Ref<DuplicateModalWrapperRef>) => {
  const [modalVisible, setModalVisible] = useState(false);
  const refCallbacks = useRef<ShowModalProps<{
    actionAdapter: Partial<ActionAdapter> & Pick<ActionAdapter, 'name'>;
  }> | null>(null);

  useImperativeHandle(ref, () => ({
    show(props) {
      refCallbacks.current = props;
      setModalVisible(true);
    },
    close: () => setModalVisible(false),
  }));

  const handleClose = (data?: { actionAdapter: Partial<ActionAdapter> & Pick<ActionAdapter, 'name'> }) => {
    if (refCallbacks.current?.subscribe && data) {
      refCallbacks.current.subscribe(data);
    }
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    refCallbacks.current = null;
  };

  if (!modalVisible) return null;

  let actionAdapter: (Partial<ActionAdapter> & Pick<ActionAdapter, 'name'>) | null = null;

  if (
    refCallbacks.current?.params &&
    typeof refCallbacks.current?.params === 'object' &&
    'actionAdapter' in refCallbacks.current?.params
  ) {
    actionAdapter = refCallbacks.current?.params.actionAdapter;
  }

  return (
    <StyledGreenHeaderModal
      content={<DuplicateModalForm onClose={handleClose} actionAdapter={actionAdapter} />}
      disableOutsideClick={true}
      modalSize={ModalSize.MEDIUM}
      onCancel={handleCloseModal}
      title="Duplicate Action Adapter"
    />
  );
});

interface DuplicateModalFormProps {
  onClose: (data?: { actionAdapter: Partial<ActionAdapter> & Pick<ActionAdapter, 'name'> }) => void;
  actionAdapter: (Partial<ActionAdapter> & Pick<ActionAdapter, 'name'>) | null;
}

const DuplicateModalForm: React.FC<DuplicateModalFormProps> = ({ onClose, actionAdapter }) => {
  const handleCancel = () => onClose();
  const handleDuplicate = () => {
    if (actionAdapter) {
      onClose({ actionAdapter });
    }
  };
  if (!actionAdapter) return null;
  return (
    <>
      <ModalContent>
        <ModalDescription>
          Are you sure you want to duplicate the action adapter <strong>{actionAdapter.name}</strong>?
        </ModalDescription>
      </ModalContent>
      <RoundedFooter>
        <Button data-aid="modal-close" onClick={handleCancel} type={ButtonTypes.SECONDARY}>
          Cancel
        </Button>

        <Button data-aid="modal-save" onClick={handleDuplicate} type={ButtonTypes.PRIMARY}>
          Duplicate
        </Button>
      </RoundedFooter>
    </>
  );
};

const StyledGreenHeaderModal = styled(EmbedModalWithGreenHeader)`
  padding: 0;
`;

const RoundedFooter = styled(StyledFooter)`
  border-radius: 0 0 ${p => p.theme.borderRadius.large} ${p => p.theme.borderRadius.large};
  border-top: 1px solid ${p => p.theme.colors.gray100};
  gap: ${p => p.theme.spacing.medium};
`;

const ModalDescription = styled.div`
  align-self: stretch;
  color: ${p => p.theme.colors.black};
  font-style: normal;
  font-weight: ${p => p.theme.fontWeight.normal};
  line-height: ${p => p.theme.lineHeight.base};
  padding: ${p => p.theme.spacing.insetLarge};
  text-align: justify;
`;

const ModalContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

export default DuplicateConfirmationModal;
